import React, { memo, PropsWithChildren } from 'react';

import { useTranslation } from 'react-i18next';
import { useId } from 'react-id-generator';
import { LoadIndicator, Popover } from 'devextreme-react';
import {
    StyledContainer,
    StyledInfoWrapper,
    StyledLabel,
    StyledValue,
    StyledLoaderWrapper,
} from './styled';
import { IdType } from 'fulogics-ui-core';

function measurementsAsString(data?: Measurements) {
    return `${data?.length || '?'} x ${data?.width || '?'}`;
}

const CarrierDetailsPopup = memo((props: CarrierDetailsPopupProps) => {
    const { t } = useTranslation('CarrierDetailsPopup');
    const [targetId] = useId();

    return (
        <>
            <div id={targetId}>{props.children}</div>
            <Popover
                target={`#${targetId}`}
                showEvent="dxhoverstart"
                hideEvent="dxhoverend"
                width="315px"
                height="auto"
                position="top"
                showTitle={false}
            >
                <StyledContainer>
                    <h5>{t('title')}</h5>
                    <div>
                        <StyledInfoWrapper>
                            <StyledLabel>{t('grid.carrierType')}</StyledLabel>
                            <StyledLabel>{t('grid.max')}</StyledLabel>
                        </StyledInfoWrapper>
                        {props?.loading ? (
                            <StyledLoaderWrapper>
                                <LoadIndicator visible />
                            </StyledLoaderWrapper>
                        ) : (
                            props?.data
                                ?.reduce((reduceArray: CarrierDetailsPopupData[], currentValue) => {
                                    if (
                                        reduceArray.findIndex(
                                            (x) =>
                                                x?.measurements?.width ===
                                                    currentValue?.measurements?.width &&
                                                x?.measurements?.length ===
                                                    currentValue?.measurements?.length
                                        ) < 0
                                    )
                                        reduceArray.push(currentValue);

                                    return reduceArray;
                                }, [])
                                .map(({ measurements, id }, index) => {
                                    const remainingSpace = props?.remainingSpace?.find(
                                        ({ carrierTypeId }) => carrierTypeId === id
                                    )?.remainingSpace;

                                    return (
                                        <StyledInfoWrapper
                                            key={`${id}-${index}`}
                                            error={!remainingSpace}
                                        >
                                            <StyledValue transparent={!!remainingSpace}>
                                                {measurementsAsString(measurements)}
                                            </StyledValue>
                                            <StyledValue>{remainingSpace || 0}</StyledValue>
                                        </StyledInfoWrapper>
                                    );
                                })
                        )}
                    </div>
                </StyledContainer>
            </Popover>
        </>
    );
});

export default CarrierDetailsPopup;

export type CarrierDetailsPopupProps = {
    data?: CarrierDetailsPopupData[];
} & PropsWithChildren<CarrierDetailsPopupDisplayOptions>;

export class CarrierDetailsPopupDisplayOptions {
    className?: string;
    loading?: boolean;
    //
    remainingSpace?: RemainingSpace[];
}

export type RemainingSpace = {
    carrierTypeId?: IdType;
    remainingSpace?: number;
};

type Measurements = {
    length?: number;
    width?: number;
};

export type CarrierDetailsPopupData = {
    id?: IdType;
    name?: string;
    measurements?: Measurements;
};

export const CarrierDetailsPopupDataKeys = [
    'id',
    'name',
    'measurements.length',
    'measurements.width',
];
